import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { DayStrip } from '../components/DayStrip';
import { COACH_TZ, fmtLongDay, fmtShortDay, fmtTime, nextDays, toCoachDateKey } from '../lib/time';
import { alert, confirm, haptic } from '../lib/telegram';
import { MoneyTab, setStatsToken } from './MoneyTab';
import { MethodPicker, methodLabel, rememberMethod, useMethod } from '../components/MethodPicker';
import { ClientsList, setClientsToken } from './ClientsList';
import { setTagToken } from '../components/TagSheet';
import { setAxisToken } from '../components/AxisSheet';
import { Dashboard, setDashToken } from './Dashboard';
import { ClientDetail, setDetailToken } from './ClientDetail';
import { setOfflineToken } from '../components/OfflineSheets';
import { Settings, setSettingsToken } from './Settings';
import { SessionView, setProfileToken } from './PlayerProfile';
import { back, backTo, open, type Layer, type Nav } from '../lib/nav-stack';
import { fmtMoney, useT } from '../lib/i18n';
import { setTimeOffToken } from './TimeOff';
import { BillingBanner, Business, ClosedScreen, setBillingToken, type Billing } from './Billing';
import { FirstStepsCard, setFirstStepsToken } from '../components/FirstSteps';
import { setDrillsToken } from '../components/Drills';

const BASE = import.meta.env.VITE_API_URL ?? '';
let authToken = '';
export const setCoachToken = (t: string) => { authToken = t; setStatsToken(t); setDashToken(t); setDetailToken(t); setSettingsToken(t); setProfileToken(t); setClientsToken(t); setTagToken(t); setOfflineToken(t); setFirstStepsToken(t); setDrillsToken(t); setAxisToken(t); setTimeOffToken(t); setBillingToken(t); };

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', authorization: `Bearer ${authToken}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

interface SessionBooking {
  bookingId: string; clientId: string; name: string; username: string | null;
  phone: string | null; status: string; paymentMode: 'credits' | 'cash';
  amountDue: number | null; paidAt: string | null; botStarted: boolean;
}
interface CoachSession {
  id: string; starts_at: string; ends_at: string; kind: string; status: string;
  location: string | null; title: string; bookings: SessionBooking[];
}
interface CoachClient {
  id: string; telegram_id: string; first_name: string; last_name: string | null;
  username: string | null; phone: string | null; bot_started: boolean;
  notes: string | null; credits: number; sessions_taken: number; last_seen: string | null;
}
interface Pkg {
  id: string; name: string; credits: number; price: number;
  currency: string; validity_days: number | null;
}
type Tab = 'home' | 'money' | 'clients' | 'settings' | 'business';

export function CoachScreen({ firstName, billing, isOwner }: { firstName: string; billing?: Billing | null; isOwner?: boolean }) {
  // The owner's bot message about a closed coach opens the app at ?tab=business.
  const [tab, setTab] = useState<Tab>(() =>
    isOwner && new URLSearchParams(window.location.search).get('tab') === 'business' ? 'business' : 'home');
  /**
   * The coach's history above the tab (lib/nav-stack.ts): a client opened
   * from the Calendar sits on top of the Calendar, which stays as it was
   * underneath, and «Назад» takes it off again.
   */
  const [stack, setStack] = useState<Nav['stack']>([]);
  const nav: Nav = { tab, stack };
  const openLayer = (layer: Layer) => {
    setStack(open(nav, layer, window.scrollY).stack);
    window.scrollTo(0, 0);
  };
  const goBack = () => {
    const r = back(nav);
    setStack(r.nav.stack);
    // The card underneath a lesson reads its lessons again: one may have changed.
    if (r.nav.stack.length) window.dispatchEvent(new Event('coach:card-refresh'));
    const restore = r.restore;
    if (restore !== null) requestAnimationFrame(() => window.scrollTo(0, restore));
  };
  /** Another tab, from the tab bar or a jump: that tab, nothing on top. */
  const switchTab = (to: Tab) => { setStack([]); setTab(to); };
  const openClient = (clientId: string, name?: string) => openLayer({ kind: 'client', clientId, name });
  // A day for the Calendar to show (from «Первые шаги» → «Горячие часы»).
  const [calendarDay, setCalendarDay] = useState<{ day: string; at: number } | null>(null);
  const [clientCount, setClientCount] = useState<number | null>(null);
  const [tagsAt, setTagsAt] = useState<number | undefined>(undefined);
  // The bot's Offer it button opens the app at ?offer=<id>.
  const [offerId, setOfferId] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('offer'));
  const closeOffer = () => {
    setOfferId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('offer');
    window.history.replaceState(null, '', url.toString());
  };

  // Fetched once here rather than inside the tab, so the number is
  // visible before you open it — which is the point of putting it on the
  // label at all.
  useEffect(() => {
    const count = () => api<{ clients: unknown[] }>('/api/coach/insights')
      .then((r) => setClientCount(r.clients.length))
      .catch(() => setClientCount(null));
    count();
    // Approving someone adds them to the list, so the number follows.
    window.addEventListener('coach:clients-changed', count);
    return () => window.removeEventListener('coach:clients-changed', count);
  }, []);

  useEffect(() => {
    const jump = () => switchTab('money');
    window.addEventListener('coach:open-money', jump);
    return () => window.removeEventListener('coach:open-money', jump);
  }, []);

  useEffect(() => {
    const jump = () => { switchTab('settings'); setTagsAt(Date.now()); };
    window.addEventListener('coach:edit-tags', jump);
    return () => window.removeEventListener('coach:edit-tags', jump);
  }, []);
  // «Первые шаги» sends the coach where a step is done: a tab, and a section on it.
  useEffect(() => {
    const go = (e: Event) => {
      const { tab: to, anchor, day } = (e as CustomEvent<{ tab: Tab; anchor?: string; day?: string }>).detail;
      switchTab(to);
      if (day) setCalendarDay({ day, at: Date.now() });
      // The tab loads its data first: wait for the section, up to about 3 s.
      if (anchor) {
        let tries = 0;
        const find = () => {
          const el = document.getElementById(anchor);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          else if (++tries < 30) setTimeout(find, 100);
        };
        setTimeout(find, 100);
      }
    };
    window.addEventListener('coach:go', go);
    return () => window.removeEventListener('coach:go', go);
  }, []);
  // The brief's «Открыть» opens the app at ?session=<id>&day=<date>: that day, at that session.
  useEffect(() => {
    const url = new URL(window.location.href);
    const session = url.searchParams.get('session'), day = url.searchParams.get('day');
    if (!session || !day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
    url.searchParams.delete('session'); url.searchParams.delete('day');
    window.history.replaceState(null, '', url.toString());
    window.dispatchEvent(new CustomEvent('coach:go', { detail: { tab: 'home', anchor: `sess-${session}`, day } }));
  }, []);
  const t = useT();
  // Switched off by the owner: the calendar only, read-only (the server
  // refuses any change).
  const off = billing?.status === 'off';
  const TABS: [Tab, string][] = [
    ['home', t('co.tab.today')],
    ['money', t('co.tab.money')],
    ['clients', clientCount === null ? t('co.tab.clients') : `${t('co.tab.clients')} ${clientCount}`],
    ['settings', t('co.tab.setup')],
    // The owner's Business tab, as an icon so the other labels keep their size.
    ...(isOwner ? [['business', '📊'] as [Tab, string]] : []),
  ];

  // Closed: the closed screen and a read-only list of booked sessions. No tabs.
  if (off && billing) {
    return (
      <>
        <div className="balance">
          <div>
            <span className="balance__count" style={{ fontSize: 19 }}>{firstName}</span>
            <span className="balance__label">{t('co.coachView')}</span>
          </div>
        </div>
        <ClosedScreen b={billing} />
      </>
    );
  }

  return (
    <>
      <div className="balance">
        <div>
          <span className="balance__count" style={{ fontSize: 19 }}>{firstName}</span>
          <span className="balance__label">{t('co.coachView')}</span>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} role="tab" className={`tab${id === 'business' ? ' tab--icon' : ''}`} aria-selected={tab === id}
            aria-label={id === 'business' ? t('biz.title') : undefined}
            onClick={() => { haptic.tap(); switchTab(id); }}>{label}</button>
        ))}
      </div>
      {billing && <BillingBanner b={billing} />}
      {/* The tab: kept as it is (day, view, list, search) while something is open on top. */}
      <div hidden={nav.stack.length > 0}>
        {/* A new coach's first steps, while there are some left (sign-up ends here with ?steps=1). */}
        {tab === 'home' && <FirstStepsCard openAtStart={new URLSearchParams(window.location.search).get('steps') === '1'} />}
        {tab === 'home' && <Dashboard jumpDay={calendarDay} onOpenClient={openClient} />}
        {tab === 'money' && <MoneyTab />}
        {tab === 'clients' && <ClientsTab onOpenClient={openClient} />}
        {tab === 'settings' && <Settings scrollToTags={tagsAt} billing={billing} />}
        {tab === 'business' && isOwner && <Business />}
      </div>
      {/* What is open on top, in order; only the last shows. «Назад» takes it off. */}
      {nav.stack.map((e, i) => (
        <div key={`${i}-${e.kind === 'client' ? e.clientId : e.bookingId}`} hidden={i !== nav.stack.length - 1}>
          {e.kind === 'client' ? (
            <ClientsTab onOpenClient={openClient} onBack={goBack}
              onOpenLesson={(bookingId) => openLayer({ kind: 'lesson', bookingId })}
              card={{ clientId: e.clientId, name: e.name,
                backLabel: t(({ calendar: 'co.profile.backCalendar', clients: 'co.profile.back', back: 'lv.back' } as const)[backTo(nav, i)]) }} />
          ) : (
            <SessionView bookingId={e.bookingId} onBack={goBack} />
          )}
        </div>
      ))}
      {offerId && <OfferSheet id={offerId} onClose={closeOffer} />}
    </>
  );
}

interface OfferData {
  id: string; status: 'asking' | 'offered' | 'taking' | 'taken' | 'kept' | 'closed';
  startsAt: string; endsAt: string;
  suggestions: OfferClient[];
}
interface OfferClient {
  id: string; name: string; group: 'regular' | 'frequent'; regular: number; sessions: number; inBot: boolean;
}

/** 0 = Monday, in the coach's time zone. */
const weekdayIndex = (d: Date) =>
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(
    new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: COACH_TZ }).format(d));

/**
 * Who gets a freed slot. The people who usually book that weekday around
 * that time come first, then the ones who come most often at any time;
 * anyone else can be found by name. Credits play no part. The server never
 * lists blocked clients or new requests. The suggested groups start
 * ticked; nobody hears anything until Offer is tapped.
 */
function OfferSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const t = useT();
  const [data, setData] = useState<OfferData | null | false>(null);
  // Another coach's slot (a message that reached the wrong coach): said plainly.
  const [notYours, setNotYours] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  // «+ Добавить клиента»: the search, and whoever was added from it.
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<OfferClient[] | null>(null);
  const [added, setAdded] = useState<OfferClient[]>([]);

  useEffect(() => {
    if (!searching || !q.trim()) { setFound(null); return; }
    const timer = setTimeout(() => {
      api<{ clients: OfferClient[] }>(`/api/coach/offers/${id}/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setFound(r.clients)).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [q, searching, id]);

  useEffect(() => {
    api<OfferData>(`/api/coach/offers/${id}`)
      .then((d) => { setData(d); setPicked(new Set(d.suggestions.filter((s) => s.inBot).map((s) => s.id))); })
      .catch((err) => { setNotYours(err?.message === 'unknown_offer'); setData(false); });
  }, [id]);

  async function send() {
    setBusy(true);
    try {
      const r = await api<{ sent: number }>(`/api/coach/offers/${id}/send`, {
        method: 'POST', body: JSON.stringify({ clientIds: [...picked] }),
      });
      haptic.success();
      await alert(t('co.offer.sent', { n: r.sent, clients: t.word('co.offer.clientsDat', r.sent) }));
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  const start = data ? new Date(data.startsAt) : null;
  const when = start ? `${fmtShortDay(start)}, ${fmtTime(start)}` : '';
  // Named from its length, as on the player's My sessions and in the bot.
  const minutes = data ? Math.round((new Date(data.endsAt).getTime() - new Date(data.startsAt).getTime()) / 60_000) : 0;
  const day = start ? weekdayIndex(start) : 0;
  const regulars = data ? data.suggestions.filter((s) => s.group === 'regular') : [];
  const frequent = data ? data.suggestions.filter((s) => s.group === 'frequent') : [];
  const listed = new Set([...(data ? data.suggestions : []), ...added].map((s) => s.id));
  const add = (c: OfferClient) => {
    haptic.tap();
    setAdded([...added, c]);
    if (c.inBot) setPicked(new Set([...picked, c.id]));
    setQ(''); setFound(null); setSearching(false);
  };

  const row = (s: OfferClient) => (
    // Never started the bot: shown so the coach knows, but the offer cannot reach them.
    <label key={s.id} className={`offerrow${s.inBot ? '' : ' offerrow--off'}`}>
      <input type="checkbox" checked={picked.has(s.id)} disabled={!s.inBot}
        onChange={(e) => {
          const next = new Set(picked);
          if (e.target.checked) next.add(s.id); else next.delete(s.id);
          setPicked(next);
        }} />
      <span className="offerrow__who">
        <b>{s.name}</b>
        <span className="offerrow__chips">
          {s.regular >= 2 && (
            <span className="offerchip offerchip--regular">
              {t('co.offer.regularChip', { days: t('co.offer.daysPl').split('|')[day], n: s.regular })}
            </span>
          )}
          {s.sessions > 0 && (
            <span className="offerchip">{t('co.offer.freqChip', { n: s.sessions, sessions: t.word('co.offer.sessionsW', s.sessions) })}</span>
          )}
          {!s.inBot && <span className="offerchip">{t('co.offer.notInBot')}</span>}
        </span>
      </span>
    </label>
  );

  return createPortal(
    <div className="sheet" onClick={onClose}>
      <div className="sheet__panel" onClick={(e) => e.stopPropagation()}>
        {data === null ? <div className="skeleton" style={{ height: 160 }} /> : data === false ? (
          <div className="card__when">{notYours ? t('co.offer.notYours') : t('co.offer.failed')}</div>
        ) : (
          <>
            <div className="sheet__title">{t('co.offer.title', { when })}</div>
            <div className="card__when">{fmtLongDay(start!)}, {fmtTime(start!)} · {t('bookings.private', { min: minutes })}</div>
            {data.status !== 'asking' ? (
              <div className="offer__state">{t(`co.offer.state.${data.status === 'taking' ? 'taken' : data.status}` as 'co.offer.state.taken')}</div>
            ) : (
              <>
                {data.suggestions.length === 0 && added.length === 0 && (
                  <div className="offer__state">{t('co.offer.nobody')}</div>
                )}
                {regulars.length > 0 && (
                  <>
                    <div className="offer__section">{t('co.offer.regulars')}</div>
                    <div className="offerlist">{regulars.map(row)}</div>
                  </>
                )}
                {frequent.length > 0 && (
                  <>
                    <div className="offer__section">{t('co.offer.frequent')}</div>
                    <div className="offerlist">{frequent.map(row)}</div>
                  </>
                )}
                {added.length > 0 && (
                  <>
                    <div className="offer__section">{t('co.offer.added')}</div>
                    <div className="offerlist">{added.map(row)}</div>
                  </>
                )}
                {searching ? (
                  <div className="offer__search">
                    <input autoFocus value={q} placeholder={t('co.offer.searchPh')}
                      onChange={(e) => setQ(e.target.value)} />
                    {found !== null && (
                      <div className="offerlist">
                        {found.filter((c) => !listed.has(c.id)).length === 0
                          ? <div className="card__when">{t('co.offer.noneFound')}</div>
                          : found.filter((c) => !listed.has(c.id)).map((c) => (
                            <button key={c.id} className={`offerfound${c.inBot ? '' : ' offerrow--off'}`} onClick={() => add(c)}>
                              <b>{c.name}</b>
                              {c.sessions > 0 && <span className="offerchip">{t('co.offer.freqChip', { n: c.sessions, sessions: t.word('co.offer.sessionsW', c.sessions) })}</span>}
                              {!c.inBot && <span className="offerchip">{t('co.offer.notInBot')}</span>}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button className="sess__link offer__add" onClick={() => { haptic.tap(); setSearching(true); }}>{t('co.offer.add')}</button>
                )}
              </>
            )}
            <div className="card__when" style={{ marginTop: 10 }}>{t('co.offer.never')}</div>
            <div className="sheet__actions">
              <button className="paybar__opt" onClick={onClose}>{t('co.offer.cancel')}</button>
              {data.status === 'asking' && [...data.suggestions, ...added].some((s) => s.inBot) && (
                <button className="savebtn" disabled={busy || picked.size === 0} onClick={send}>
                  {t('co.offer.send', { n: picked.size })}
                </button>
              )}
            </div>
            {data.status === 'asking' && <div className="card__when" style={{ textAlign: 'center' }}>{t('co.offer.foot')}</div>}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Clients: the list, with a client's own page on top of it.
 *
 * The list says how often each person trains, where their level is and
 * whether this month moved — the things a coach would say about them —
 * and puts whoever needs chasing at the top.
 */
/**
 * The Clients tab's list, or — given `card` — one client's card, opened on
 * top of whatever the coach was looking at (CoachScreen's history). The
 * card's «Продать пакет» screens live here either way.
 */
function ClientsTab({ card, onOpenClient, onBack, onOpenLesson }: {
  card?: { clientId: string; name?: string; backLabel: string };
  onOpenClient: (id: string, name?: string) => void;
  onBack?: () => void;
  onOpenLesson?: (bookingId: string) => void;
}) {
  const t = useT();
  const [granting, setGranting] = useState<any | null>(null);
  const [customGrant, setCustomGrant] = useState(false);
  const [customCredits, setCustomCredits] = useState('');
  const [customPaid, setCustomPaid] = useState('');
  const [packages, setPackages] = useState<Pkg[]>([]);
  // «Сколько получено, €» for a package from the list: optional. Empty is
  // a gift, as before — no money counted, no method asked.
  const [received, setReceived] = useState('');
  // How it was paid, once there is an amount (components/MethodPicker.tsx).
  const [method, setMethod] = useMethod();
  const receivedAmount = Number(received.replace(',', '.'));
  const hasReceived = Number.isFinite(receivedAmount) && receivedAmount > 0;

  useEffect(() => {
    api<Pkg[]>('/api/coach/packages').then(setPackages).catch(() => setPackages([]));
  }, []);

  /**
   * A package that is not on the price list.
   *
   * Someone hands over €1,600 for twenty sessions and there is no preset
   * for it. The rate follows from the two numbers — €80 each here — and
   * everything downstream (earnings, what you owe, expiry) treats it
   * exactly like a listed package.
   */
  async function grantCustom(client: any) {
    const credits = Number(customCredits);
    const paid = Number(customPaid);
    if (!Number.isFinite(credits) || credits <= 0) {
      await alert(t('co.sell.howMany'));
      return;
    }
    if (!Number.isFinite(paid) || paid < 0) {
      await alert(t('co.sell.howMuch'));
      return;
    }

    const ok = await confirm(
      t('co.sell.confirmCustom', {
        n: credits, paid, each: (paid / credits).toFixed(0),
      }) + (paid > 0 ? `\n\n${t('pm.label')}: ${methodLabel(t, method)}` : ''));
    if (!ok) return;

    try {
      const res = await api<{ balance: number }>('/api/coach/grant', {
        method: 'POST',
        body: JSON.stringify({ client_id: client.id, credits, amount_paid: paid, ...(paid > 0 ? { method } : {}) }),
      });
      if (paid > 0) rememberMethod(method);
      haptic.success();
      await alert(t('co.sell.done', { n: res.balance }));
      setGranting(null);
      setCustomGrant(false);
      setCustomCredits('');
      setCustomPaid('');
    } catch (err: any) {
      haptic.error();
      await alert(t('co.sell.failed', { message: err.message }));
    }
  }

  async function grant(client: any, pkg: Pkg) {
    const amount = hasReceived ? Math.round(receivedAmount * 100) / 100 : null;
    const ok = await confirm(
      t('co.sell.confirmPackage', { n: pkg.credits, price: pkg.price })
      + (amount ? `\n\n${t('co.sell.receivedLine', { amount: fmtMoney(amount), method: methodLabel(t, method) })}` : ''));
    if (!ok) return;
    try {
      const res = await api<{ granted: number; balance: number }>('/api/coach/grant', {
        method: 'POST',
        body: JSON.stringify({ client_id: client.id, package_id: pkg.id, ...(amount ? { amount_paid: amount, method } : {}) }),
      });
      if (amount) rememberMethod(method);
      haptic.success();
      await alert(t('co.sell.done', { n: res.balance }));
      setGranting(null);
      setReceived('');
    } catch (err: any) {
      haptic.error();
      await alert(t('co.sell.failed', { message: err.message }));
    }
  }

  if (granting) {
    return (
      <>
        <div className="section-title">{t('co.sell.title', { name: granting.first_name ?? granting.name })}</div>
        {!customGrant && (
          <div style={{ padding: '0 16px' }}>
            <label className="field">
              <span className="field__label">{t('co.sell.received')}<small>{t('co.sell.receivedHint')}</small></span>
              <span className="field__input">
                <input type="number" inputMode="decimal" value={received} placeholder="0"
                  onChange={(e) => setReceived(e.target.value)} />
                <em>€</em>
              </span>
            </label>
            {hasReceived && <MethodPicker value={method} onChange={setMethod} />}
          </div>
        )}
        <div className="list">
          {packages.map((p) => (
            <button key={p.id} className="card" style={{ width: '100%', textAlign: 'left' }}
              onClick={() => grant(granting, p)}>
              <div className="card__head">
                <span className="card__title">{p.name}</span>
                <span className="card__when">{fmtMoney(p.price)}</span>
              </div>
              <div className="card__when" style={{ marginTop: 3 }}>
                {t('co.sell.eachPrice', {
                  n: p.credits, sessions: t.word('co.today.sessions', Number(p.credits)),
                  each: (Number(p.price) / Number(p.credits)).toFixed(0),
                })}
                {p.validity_days
                  ? t('co.sell.expiresIn', {
                      n: p.validity_days, days: t.word('co.days', Number(p.validity_days)),
                    })
                  : t('co.sell.noExpiry')}
              </div>
            </button>
          ))}
          {customGrant ? (
            <div className="card">
              <div className="card__title">{t('co.sell.somethingElse')}</div>
              <label className="field">
                <span className="field__label">{t('co.sell.sessionsLabel')}</span>
                <span className="field__input">
                  <input type="number" inputMode="decimal" value={customCredits}
                    onChange={(e) => setCustomCredits(e.target.value)} />
                </span>
              </label>
              <label className="field">
                <span className="field__label">{t('co.sell.theyPaid')}</span>
                <span className="field__input">
                  <input type="number" inputMode="decimal" value={customPaid}
                    onChange={(e) => setCustomPaid(e.target.value)} />
                  <em>€</em>
                </span>
              </label>
              {Number(customCredits) > 0 && Number(customPaid) > 0 && (
                <div className="card__when" style={{ marginTop: 4 }}>
                  {t('co.sell.aSession', { amount: (Number(customPaid) / Number(customCredits)).toFixed(0) })}
                </div>
              )}
              {Number(customPaid) > 0 && <MethodPicker value={method} onChange={setMethod} />}
              <button className="savebtn" onClick={() => grantCustom(granting)}>
                {t('co.sell.addSessions')}
              </button>
              <button className="paybar__opt" style={{ width: '100%', marginTop: 8 }}
                onClick={() => setCustomGrant(false)}>{t('co.sell.backToPackages')}</button>
            </div>
          ) : (
            <button className="paybar__opt" style={{ width: '100%' }}
              onClick={() => { haptic.tap(); setCustomGrant(true); }}>
              {t('co.sell.somethingElse')}
            </button>
          )}

          <button className="paybar__opt" style={{ width: '100%', marginTop: 8 }}
            onClick={() => { setGranting(null); setCustomGrant(false); }}>{t('co.pkg.back')}</button>
        </div>
      </>
    );
  }

  if (card) {
    return (
      <ClientDetail key={card.clientId}
        clientId={card.clientId}
        preview={{ name: card.name }}
        backLabel={card.backLabel}
        onBack={() => onBack?.()}
        onSell={(client) => setGranting(client)}
        onOpenClient={onOpenClient}
        onOpenLesson={onOpenLesson}
      />
    );
  }

  return <ClientsList onOpenClient={onOpenClient} />;
}
